import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  ScanLine,
  RefreshCw,
  Settings,
  Upload,
  X,
  Loader2,
  ShieldAlert,
} from "lucide-react";

import Sidebar from "../components/ui/Sidebar";
import { DocumentDropzone } from "../components/documents/DocumentDropzone";
import { LiveExtractionQueue } from "../components/documents/LiveExtractionQueue";
import { SmartInbox } from "../components/documents/SmartInbox";
import { DashboardSummary } from "../components/documents/DashboardSummary";
import { DocumentDetailPanel } from "../components/documents/DocumentDetailPanel";
import { useResolveOcrAccount } from "../hooks/useResolveOcrAccount";
import type { DocumentRow } from "../../types/documents";

export default function OcrTrackingPage() {
  // Resolve the OCR X-User-Id once per session (POST /document_extraction/account).
  // The result is cached at module scope in services/ocrApi.ts so every
  // subsequent request below carries the correct X-User-Id header. The
  // tracking widgets below are gated on `xUserId` so they don't fire their
  // queries before the header is available — otherwise the backend returns
  // empty results for the un-authed shape and the inbox loads blank.
  const { xUserId, isResolving, error: resolveError, gate } = useResolveOcrAccount();
  const ocrReady = xUserId != null;
  const orgUnmapped =
    gate.state === "missing-claims" && gate.missingOrgId;

  const queryClient = useQueryClient();
  const [openDoc, setOpenDoc] = useState<DocumentRow | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["ocr", "tracking"] }),
        queryClient.invalidateQueries({ queryKey: ["ocr", "dashboard"] }),
      ]);
    } finally {
      setTimeout(() => setRefreshing(false), 400);
    }
  };

  return (
    // h-screen + overflow-hidden so the inner regions scroll independently.
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <Sidebar />

      <div className="flex-1 ml-16 mr-4 mt-4 mb-4 min-w-0 min-h-0">
        <div className="h-full bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col min-h-0">
          {/* Branded header -- "Upload document" is the primary CTA here. */}
          <header className="bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <ScanLine size={20} className="text-blue-600" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">OCR Tracking</h1>
                  <p className="text-sm text-gray-500">
                    Drop a document, watch the pipeline live, and triage extracted results.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setUploadOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium shadow-sm hover:bg-blue-700 transition-colors"
                >
                  <Upload size={14} />
                  Upload document
                </button>
                <Link
                  to="/studio/templateEngine"
                  className="inline-flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
                >
                  <Settings size={14} className="text-gray-500" />
                  <span className="text-gray-700">Template Engine</span>
                </Link>
                <button
                  type="button"
                  onClick={refreshAll}
                  className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  disabled={refreshing}
                  aria-label="Refresh"
                >
                  <RefreshCw size={18} className={refreshing ? "animate-spin" : ""} />
                </button>
              </div>
            </div>
          </header>

          {/* Body -- main content + right rail */}
          <div className="flex flex-1 overflow-hidden min-h-0">
            <div className="flex-1 p-6 overflow-auto min-w-0 min-h-0">
              {/* Gate the data widgets on the /account resolve completing.
                  Without this, DashboardSummary + SmartInbox fire their
                  queries in parallel with the resolve and the requests
                  go out before _resolvedXUserId is populated → backend
                  returns no rows. We only mount the data widgets after
                  xUserId is known. */}
              {ocrReady ? (
                <div className="space-y-6">
                  <DashboardSummary />
                  {/* The inline dropzone & queue grid is gone -- upload is
                      now triggered from the header button, which opens a
                      modal containing the dropzone + the live queue. The
                      Smart Inbox now claims the full content width and is
                      the primary focus of the page. */}
                  <SmartInbox onOpenDocument={(doc) => setOpenDoc(doc)} />
                </div>
              ) : orgUnmapped ? (
                // Inline empty state mirroring the toast — both render so
                // the user still sees context if they dismissed the toast.
                <div className="flex h-full flex-col items-center justify-center text-center px-6 py-16">
                  <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-rose-50 text-rose-600 ring-1 ring-rose-200">
                    <ShieldAlert className="h-7 w-7" />
                  </div>
                  <h2 className="text-base font-semibold text-gray-900">
                    Your account isn't mapped to an organization
                  </h2>
                  <p className="mt-2 max-w-md text-sm text-gray-500">
                    OCR features can't load until your administrator maps
                    your account to an organization. After they do, sign
                    out and back in to pick up the change.
                  </p>
                </div>
              ) : resolveError ? (
                <div className="flex h-full flex-col items-center justify-center text-center px-6 py-16">
                  <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-rose-50 text-rose-600 ring-1 ring-rose-200">
                    <ShieldAlert className="h-7 w-7" />
                  </div>
                  <h2 className="text-base font-semibold text-gray-900">
                    Couldn't load your OCR account
                  </h2>
                  <p className="mt-2 max-w-md text-sm text-gray-500">
                    {resolveError.message ||
                      "We couldn't reach the account service. Try refreshing the page."}
                  </p>
                </div>
              ) : (
                // Default state: account resolve in flight.
                <div className="flex h-full flex-col items-center justify-center text-center px-6 py-16">
                  <Loader2 className="h-7 w-7 animate-spin text-blue-600" />
                  <p className="mt-3 text-sm font-medium text-gray-700">
                    {isResolving ? "Loading your account…" : "Initializing…"}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Setting up your OCR session before loading documents.
                  </p>
                </div>
              )}
            </div>

            {/* Right rail -- document detail panel */}
            <div
              className={`bg-white border-l border-gray-200 flex flex-col min-h-0 transition-all duration-300 ease-in-out ${
                openDoc ? "w-96 opacity-100" : "w-0 opacity-0 overflow-hidden"
              }`}
            >
              {openDoc ? (
                <DocumentDetailPanel doc={openDoc} onClose={() => setOpenDoc(null)} />
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* === Upload modal ================================================
          Keeps the original DocumentDropzone + LiveExtractionQueue
          components co-located in a focused overlay. Pressing Escape or
          clicking the backdrop closes it. */}
      {uploadOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-sm p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Upload document"
          onClick={(e) => {
            if (e.target === e.currentTarget) setUploadOpen(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") setUploadOpen(false);
          }}
          tabIndex={-1}
        >
          <div className="w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-2xl bg-white shadow-2xl flex flex-col">
            {/* Modal header */}
            <header className="flex items-center justify-between border-b border-gray-200 px-6 py-4 flex-shrink-0">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50">
                  <Upload className="h-4 w-4 text-blue-600" />
                </span>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Upload document</h2>
                  <p className="text-xs text-gray-500">
                    Drop one or more files. Pipeline progress shows in the live queue on the right.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setUploadOpen(false)}
                className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close upload dialog"
              >
                <X className="h-5 w-5" />
              </button>
            </header>

            {/* Modal body -- dropzone + live queue side by side on lg, stacked below */}
            <div className="flex-1 overflow-auto p-6">
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div className="lg:col-span-2">
                  <DocumentDropzone
                    onUploaded={() => {
                      // Auto-close the modal as soon as a file has been
                      // accepted by the pipeline. The Smart inbox is
                      // already being refetched by the dropzone's
                      // invalidate call, so the new row will appear in
                      // the table behind the closed modal.
                      setUploadOpen(false);
                    }}
                  />
                </div>
                <div>
                  <LiveExtractionQueue />
                </div>
              </div>
            </div>

            {/* Modal footer */}
            <footer className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => setUploadOpen(false)}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Done
              </button>
            </footer>
          </div>
        </div>
      ) : null}
    </div>
  );
}
